import { AppointmentDTO } from '@/models/AppointmentDTO';
import { CenterAvailableDTO } from '@/models/CenterDTO';
import { Sex } from '@/models/constants/enums';
import { ParticipantDTO } from '@/models/ParticipantDTO';
import Vue from 'vue';
import Vuex from 'vuex';
import { ActionTypes, GetterTypes, MutationTypes } from './types';
import moment from 'moment';
import { McsisConstants } from '@/models/constants/McsisConstants';

Vue.use(Vuex);

function initialState() {
    return {
        /* Authentication */
        token: '',
        tokenTimestamp: 0,
        isIdentified: false,
        isRdvModified: false,

        /* App */
        appVersion: {
            isLatest: false,
            lastCheckDate: moment().format(McsisConstants.DefaultDateFormat),
        },
        isAppLoaded: false,
        language: 1,
        dataLoading: false,

        /* Parameters */
        parameters: {
            useLongAppointment: null as boolean | null,
            useTRMGenderChoice: null as boolean | null,
            allowFirstAppointment: null as boolean | null,
            isFirstAppointment: null as boolean | null,
            showAppointmentParticipantStep: null as boolean | null,
            isDeleted: null as boolean | null,
        },

        /* Participant */
        participant: {} as ParticipantDTO,
        isLanguagesLoaded: false,

        /* Appointment */
        appointment: {} as AppointmentDTO,

        /* Appointment selection */
        centerWithAvailabilities: [],
        centerSelected: {} as CenterAvailableDTO,
        hasEventSelected: false,

        /* Messages */
        showErrInfoModal: false,
        popUpErrMessage: '',
        maxVisitedStepNumber: 0,
    };
}

const store = new Vuex.Store({
    state: initialState(),

    getters: {
        [GetterTypes.token]: (state) => state.token,
        [GetterTypes.tokenTimestamp]: (state) => state.tokenTimestamp,
        [GetterTypes.isIdentified]: (state) => state.isIdentified,
        [GetterTypes.appVersion]: (state) => state.appVersion,
        [GetterTypes.isAppLoaded]: (state) => state.isAppLoaded,

        [GetterTypes.dataLoading]: (state) => state.dataLoading,
        [GetterTypes.isLanguagesLoaded]: (state) => state.isLanguagesLoaded,
        [GetterTypes.showErrInfoModal]: (state) => state.showErrInfoModal,
        [GetterTypes.popUpErrMessage]: (state) => state.popUpErrMessage,
        [GetterTypes.maxVisitedStepNumber]: (state) => state.maxVisitedStepNumber,
        // Participant
        [GetterTypes.participant]: (state) => state.participant,
        [GetterTypes.participantId]: (state) => state.participant.partId,
        [GetterTypes.screeningUnit]: (state) => state.participant.suId,

        // Parameters
        [GetterTypes.useLongAppointment]: (state) => state.parameters.useLongAppointment,
        [GetterTypes.useTRMGenderChoice]: (state) => state.parameters.useTRMGenderChoice,
        [GetterTypes.allowFirstAppointment]: (state) => state.parameters.allowFirstAppointment,
        [GetterTypes.isFirstAppointment]: (state) => state.parameters.isFirstAppointment,
        [GetterTypes.showAppointmentParticipantStep]: (state) => state.parameters.showAppointmentParticipantStep,
        [GetterTypes.isDeleted]: (state) => state.parameters.isDeleted,

        // Appointment
        [GetterTypes.appointment]: (state) => state.appointment,

        // Appointment selection
        [GetterTypes.reasonLongAppointment]: (state) =>
            state.appointment.appointmentDetails ? state.appointment.appointmentDetails.longAppointmentReasonsId : [],
        [GetterTypes.centerWithAvailabilities]: (state) => state.centerWithAvailabilities,
        [GetterTypes.centerSelected]: (state) => state.centerSelected,
        [GetterTypes.hasEventSelected]: (state) => state.hasEventSelected,
        [GetterTypes.appID]: (state) => state.appointment.appId,
        [GetterTypes.isRdvLong]: (state) =>
            state.appointment.appointmentDetails ? state.appointment.appointmentDetails.isLongAppointment : false,
        [GetterTypes.isRdvModified]: (state) => state.isRdvModified,
        [GetterTypes.hasRdv]: (state) => state.appointment.appId != null,
    },
    mutations: {
        [MutationTypes.setToken](state, value: string) {
            state.token = value;
        },
        [MutationTypes.setTokenTimestamp](state, timestamp: number) {
            state.tokenTimestamp = timestamp;
        },

        [MutationTypes.setIsIdentified](state, value: boolean) {
            state.isIdentified = value;
        },
        [MutationTypes.setDataLoading](state, value: boolean) {
            state.dataLoading = value;
        },
        [MutationTypes.setIsLanguagesLoaded](state, value: boolean) {
            state.isLanguagesLoaded = value;
        },
        [MutationTypes.setIsAppLoaded](state, value: boolean) {
            state.isAppLoaded = value;
        },
        [MutationTypes.setShowErrInfoModal](state, value: boolean) {
            state.showErrInfoModal = value;
        },
        [MutationTypes.setPopUpErrMessage](state, value: string) {
            state.popUpErrMessage = value;
        },
        [MutationTypes.setMaxVisitedStepNumber](state, value: number) {
            state.maxVisitedStepNumber = value;
        },
        [MutationTypes.setAppVersion](state, value: any) {
            state.appVersion = value;
        },

        // Participant
        [MutationTypes.setParticipant](state, value: ParticipantDTO) {
            state.participant = value;
        },

        // Parameters
        [MutationTypes.setUseLongAppointment](state, value: boolean) {
            state.parameters.useLongAppointment = value;
        },
        [MutationTypes.setUseTRMGenderChoice](state, value: boolean) {
            state.parameters.useTRMGenderChoice = value;
        },
        [MutationTypes.setAllowFirstAppointment](state, value: boolean) {
            state.parameters.allowFirstAppointment = value;
        },
        [MutationTypes.setIsFirstAppointment](state, value: boolean) {
            state.parameters.isFirstAppointment = value;
        },
        [MutationTypes.setShowAppointmentParticipantStep](state, value: boolean) {
            state.parameters.showAppointmentParticipantStep = value;
        },
        [MutationTypes.setIsDeleted](state, value: boolean) {
            state.parameters.isDeleted = value;
        },

        // Appointment
        [MutationTypes.setAppointment](state, value: AppointmentDTO) {
            // if (state.appointment.appId != null) {
            //     state.isRdvModified = true;
            // }
            state.appointment = value;
        },
        [MutationTypes.setInit](state) {
            Object.assign(state, initialState());
        },
        [MutationTypes.setIsRdvModified](state, value: boolean) {
            state.isRdvModified = value;
        },

        // Appointment selection
        [MutationTypes.setCenterWithAvailabilities](state, value: any) {
            state.centerWithAvailabilities = value;
        },
        [MutationTypes.setCenterSelected](state, value: any) {
            state.centerSelected = value;
        },
        [MutationTypes.setHasEventSelected](state, value: any) {
            state.hasEventSelected = value;
        },
        [MutationTypes.setAppMobile](state, value: any) {
            state.appointment.appMobile = value;
        },
        [MutationTypes.setAppEmail](state, value: any) {
            state.appointment.appEmail = value;
        },
    },
    actions: {
        [ActionTypes.setToken](context, value) {
            context.commit(MutationTypes.setToken, value);
        },
        [ActionTypes.setTokenTimestamp](context, value) {
            context.commit(MutationTypes.setTokenTimestamp, value);
        },
        [ActionTypes.setIsIdentified](context, value) {
            context.commit(MutationTypes.setIsIdentified, value);
        },
        [ActionTypes.setScreeningUnit](context, value) {
            context.commit(MutationTypes.setScreeningUnit, value);
        },
        [ActionTypes.setIsLanguagesLoaded](context, value) {
            context.commit(MutationTypes.setIsLanguagesLoaded, value);
        },
        [ActionTypes.setIsAppLoaded](context, value) {
            context.commit(MutationTypes.setIsAppLoaded, value);
        },
        [ActionTypes.setShowErrInfoModal](context, value) {
            context.commit(MutationTypes.setShowErrInfoModal, value);
        },
        [ActionTypes.setPopUpErrMessage](context, value) {
            context.commit(MutationTypes.setPopUpErrMessage, value);
        },
        [ActionTypes.setDataLoading](context, value) {
            context.commit(MutationTypes.setDataLoading, value);
        },
        [ActionTypes.setMaxVisitedStepNumber](context, value) {
            context.commit(MutationTypes.setMaxVisitedStepNumber, value);
        },
        [ActionTypes.setTokenTimestamp](context, value) {
            context.commit(MutationTypes.setTokenTimestamp, value);
        },

        // Participant
        [ActionTypes.setParticipant](context, value) {
            context.commit(MutationTypes.setParticipant, value);
        },

        // Parameters
        [ActionTypes.setUseLongAppointment](context, value) {
            context.commit(MutationTypes.setUseLongAppointment, value);
        },
        [ActionTypes.setUseTRMGenderChoice](context, value) {
            context.commit(MutationTypes.setUseTRMGenderChoice, value);
        },
        [ActionTypes.setAllowFirstAppointment](context, value) {
            context.commit(MutationTypes.setAllowFirstAppointment, value);
        },
        [ActionTypes.setShowAppointmentParticipantStep](context, value) {
            context.commit(MutationTypes.setShowAppointmentParticipantStep, value);
        },
        [ActionTypes.setIsDeleted](context, value) {
            context.commit(MutationTypes.setIsDeleted, value);
        },

        // Appointment
        [ActionTypes.setAppointment](context, value) {
            context.commit(MutationTypes.setAppointment, value);
        },
        [ActionTypes.setIsFirstAppointment](context, value) {
            context.commit(MutationTypes.setIsFirstAppointment, value);
        },
        [ActionTypes.setReasonLongAppointment](context, value) {
            context.commit(MutationTypes.setReasonLongAppointment, value);
        },
        [ActionTypes.setInit](context) {
            context.commit(MutationTypes.setInit);
        },
        [ActionTypes.setIsRdvModified](context, value) {
            context.commit(MutationTypes.setIsRdvModified, value);
        },

        // Appointment Selection
        [ActionTypes.setCenterWithAvailabilities](context, value) {
            context.commit(MutationTypes.setCenterWithAvailabilities, value);
        },
        [ActionTypes.setCenterSelected](context, value) {
            context.commit(MutationTypes.setCenterSelected, value);
        },
        [ActionTypes.setHasEventSelected](context, value) {
            context.commit(MutationTypes.setHasEventSelected, value);
        },
        [ActionTypes.setIsBtnBackVisible](context, value) {
            context.commit(MutationTypes.SetIsBtnBackVisible, value);
        },
        [ActionTypes.setTrmSex](context, value) {
            context.commit(MutationTypes.setTrmSex, value);
        },
        [ActionTypes.setAppID](context, value) {
            context.commit(MutationTypes.setAppID, value);
        },
        [ActionTypes.setAppMobile](context, value) {
            context.commit(MutationTypes.setAppMobile, value);
        },
        [ActionTypes.setAppEmail](context, value) {
            context.commit(MutationTypes.setAppEmail, value);
        },
    },
});

export default store;
